import { useSiteContent } from '../../hooks/useSiteContent';

export default function Footer() {
  const { content, loading } = useSiteContent();

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading || !content) {
    return (
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">Cargando...</p>
        </div>
      </footer>
    );
  }

  const { footer } = content;
  const enabledSocialNetworks = footer.social_networks.filter(sn => sn.enabled);

  const getSocialIcon = (network: string) => {
    const icons: { [key: string]: string } = {
      facebook: 'ri-facebook-fill',
      instagram: 'ri-instagram-fill',
      twitter: 'ri-twitter-x-fill',
      linkedin: 'ri-linkedin-fill',
      tiktok: 'ri-tiktok-fill',
      whatsapp: 'ri-whatsapp-fill',
      youtube: 'ri-youtube-fill'
    };
    return icons[network.toLowerCase()] || 'ri-link';
  };

  const getSocialColor = (network: string) => {
    const colors: { [key: string]: string } = {
      facebook: 'hover:bg-blue-600',
      instagram: 'hover:bg-pink-600',
      twitter: 'hover:bg-blue-400',
      linkedin: 'hover:bg-blue-700',
      tiktok: 'hover:bg-black',
      whatsapp: 'hover:bg-green-600',
      youtube: 'hover:bg-red-600'
    };
    return colors[network.toLowerCase()] || 'hover:bg-gray-600';
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <img 
              src="https://static.readdy.ai/image/e50d925263add9c47b947a5709171c8c/7b8bc41f7bc46c6a685a4edbcf687887.png" 
              alt="Axloweb Logo" 
              className="h-12 w-auto mb-4 object-contain"
            />
            <p className="text-gray-400 mb-4">
              Agencia de Marketing Digital especializada en hacer crecer tu negocio en línea
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="flex items-center space-x-2 bg-gray-800 px-3 py-2 rounded-lg">
                <i className="ri-google-fill text-blue-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="text-xs font-semibold text-gray-300">Google Partner</span>
              </div>
              <div className="flex items-center space-x-2 bg-gray-800 px-3 py-2 rounded-lg">
                <i className="ri-meta-fill text-blue-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="text-xs font-semibold text-gray-300">Meta Partner</span>
              </div>
            </div>
            {enabledSocialNetworks.length > 0 && (
              <div className="flex space-x-4">
                {enabledSocialNetworks.map((social) => (
                  <a
                    key={social.network}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 bg-gray-800 ${getSocialColor(social.network)} rounded-full flex items-center justify-center transition-colors cursor-pointer`}
                  >
                    <i className={`${getSocialIcon(social.network)} text-xl w-5 h-5 flex items-center justify-center`}></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Enlaces Rápidos</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                  Inicio
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                  Nosotros
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                  Servicios
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('portfolio')} className="text-gray-400 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                  Portafolio
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                  Contacto
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Contacto</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <i className="ri-map-pin-line text-blue-500 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                <span className="text-gray-400 text-sm">{footer.footer_address}</span>
              </li>
              <li className="flex items-start space-x-2">
                <i className="ri-phone-line text-green-500 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                <a href={`tel:${footer.footer_phone}`} className="text-gray-400 hover:text-white text-sm cursor-pointer">
                  {footer.footer_phone}
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <i className="ri-mail-line text-purple-500 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                <a href={`mailto:${footer.footer_email}`} className="text-gray-400 hover:text-white text-sm cursor-pointer">
                  {footer.footer_email}
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <i className="ri-time-line text-orange-500 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                <span className="text-gray-400 text-sm whitespace-pre-line">{footer.footer_business_hours}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm text-center md:text-left">
            © {new Date().getFullYear()} Axloweb. Todos los derechos reservados.
          </p>
          <a 
            href="https://readdy.ai/?origin=logo" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            Powered by Readdy
          </a>
        </div>
      </div>
    </footer>
  );
}
